import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import type React from "react";
import { useMemo, useState } from "react";
import { EmptyState } from "@/components/ui";
import type { TestRunLogs } from "../types";

interface LogsViewerProps {
  logs: TestRunLogs[];
  showExpandButton?: boolean;
}

const logTypeToColor: Record<
  string,
  "default" | "error" | "warning" | "info" | "success" | "primary"
> = {
  log: "default",
  error: "error",
  warn: "warning",
  warning: "warning",
  info: "info",
  debug: "primary",
};

export const LogsViewer: React.FC<LogsViewerProps> = ({ logs, showExpandButton = true }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [expandedModalOpen, setExpandedModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const logTypes = useMemo(() => {
    const types = new Set(logs.map((log) => log.type ?? "log"));
    return Array.from(types).sort();
  }, [logs]);

  const handleTypeToggle = (_: React.MouseEvent<HTMLElement>, newTypes: string[]) => {
    setSelectedTypes(newTypes);
  };

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        log.text.toLowerCase().includes(searchLower) ||
        (log.type ?? "log").toLowerCase().includes(searchLower);

      const matchesType = selectedTypes.length === 0 || selectedTypes.includes(log.type ?? "log");

      return matchesSearch && matchesType;
    });
  }, [logs, searchTerm, selectedTypes]);

  const logStats = useMemo(() => {
    const stats: Record<string, number> = {};
    logs.forEach((log) => {
      const type = log.type ?? "log";
      stats[type] = (stats[type] || 0) + 1;
    });
    return stats;
  }, [logs]);

  const copyLog = (log: TestRunLogs) => {
    const logText = `${format(new Date(log.created_at), "HH:mm:ss.SSS")} [${(log.type ?? "log").toUpperCase()}]: ${log.text}`;
    navigator.clipboard.writeText(logText);
    enqueueSnackbar("Log copied to clipboard", { variant: "success" });
  };

  const copyAllFilteredLogs = () => {
    const logsText = filteredLogs
      .map(
        (log) =>
          `${format(new Date(log.created_at), "HH:mm:ss.SSS")} [${(log.type ?? "log").toUpperCase()}]: ${log.text}`,
      )
      .join("\n");
    navigator.clipboard.writeText(logsText);
    enqueueSnackbar(
      `${filteredLogs.length} log${filteredLogs.length !== 1 ? "s" : ""} copied to clipboard`,
      { variant: "success" },
    );
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header with search and filters */}
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="Search logs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
            }}
          />
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={copyAllFilteredLogs}
            size="small"
            disabled={filteredLogs.length === 0}
          >
            Copy All ({filteredLogs.length})
          </Button>
          {showExpandButton && (
            <Tooltip title="Expand view">
              <IconButton size="small" onClick={() => setExpandedModalOpen(true)}>
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Filter by type:
            </Typography>
            <ToggleButtonGroup
              value={selectedTypes}
              onChange={handleTypeToggle}
              aria-label="log type filter"
              size="small"
              sx={{ flexWrap: "wrap" }}
            >
              {logTypes.map((type) => (
                <ToggleButton key={type} value={type} aria-label={type}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <span>{type.toUpperCase()}</span>
                    <Chip
                      label={logStats[type] || 0}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: "0.6rem",
                        ml: 0.5,
                      }}
                    />
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Chip label={`Total: ${logs.length}`} size="small" variant="outlined" />
            <Chip label={`Filtered: ${filteredLogs.length}`} size="small" color="primary" />
          </Stack>
        </Stack>
      </Stack>

      {/* Content */}
      <Paper variant="outlined" sx={{ overflow: "hidden", flex: 1 }}>
        {filteredLogs.length === 0 ? (
          <EmptyState message="No logs found." sx={{ p: 3 }} />
        ) : (
          <TableContainer sx={{ maxHeight: "600px" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "140px" }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "120px" }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Message</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "80px" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogs.map((log, index) => {
                  const logType = log.type ?? "log";
                  return (
                    <TableRow key={`${log.id}-${index}`} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.85em",
                            fontFamily: "monospace",
                          }}
                        >
                          {format(new Date(log.created_at), "HH:mm:ss.SSS")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={logType.toUpperCase()}
                          size="small"
                          color={logTypeToColor[logType] || "default"}
                          sx={{ height: 22, fontSize: "0.75rem" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.85em",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {log.text}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Copy log">
                          <IconButton size="small" onClick={() => copyLog(log)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Expanded Modal */}
      {showExpandButton && (
        <Dialog
          open={expandedModalOpen}
          onClose={() => setExpandedModalOpen(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { height: "85vh", maxHeight: "85vh" },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Console Logs</Typography>
            <IconButton onClick={() => setExpandedModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 2,
              height: "calc(100% - 64px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header with search and filters inside modal */}
            <Stack spacing={2} sx={{ mt: 1, mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Search logs"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={copyAllFilteredLogs}
                  size="small"
                  disabled={filteredLogs.length === 0}
                >
                  Copy All ({filteredLogs.length})
                </Button>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Filter by type:
                  </Typography>
                  <ToggleButtonGroup
                    value={selectedTypes}
                    onChange={handleTypeToggle}
                    aria-label="log type filter"
                    size="small"
                    sx={{ flexWrap: "wrap" }}
                  >
                    {logTypes.map((type) => (
                      <ToggleButton key={type} value={type} aria-label={type}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>{type.toUpperCase()}</span>
                          <Chip
                            label={logStats[type] || 0}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: "0.6rem",
                              ml: 0.5,
                            }}
                          />
                        </Stack>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Chip label={`Total: ${logs.length}`} size="small" variant="outlined" />
                  <Chip label={`Filtered: ${filteredLogs.length}`} size="small" color="primary" />
                </Stack>
              </Stack>
            </Stack>

            {/* Content inside modal */}
            <Paper variant="outlined" sx={{ overflow: "hidden", flex: 1 }}>
              {filteredLogs.length === 0 ? (
                <EmptyState message="No logs found." sx={{ p: 3 }} />
              ) : (
                <TableContainer sx={{ maxHeight: "100%" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", width: "140px" }}>Timestamp</TableCell>
                        <TableCell sx={{ fontWeight: "bold", width: "120px" }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Message</TableCell>
                        <TableCell sx={{ fontWeight: "bold", width: "80px" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLogs.map((log, index) => {
                        const logType = log.type ?? "log";
                        return (
                          <TableRow key={`modal-${log.id}-${index}`} hover>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.85em",
                                  fontFamily: "monospace",
                                }}
                              >
                                {format(new Date(log.created_at), "HH:mm:ss.SSS")}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={logType.toUpperCase()}
                                size="small"
                                color={logTypeToColor[logType] || "default"}
                                sx={{ height: 22, fontSize: "0.75rem" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.85em",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {log.text}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Copy log">
                                <IconButton size="small" onClick={() => copyLog(log)}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};
